/**
 * Markdown table rendered as a grid in the prose flow, with export controls
 * beneath it.
 *
 * The table is a figure in the answer, not a panel bolted into it, so it gets
 * no card, no border and no header chrome — only the block spacing a paragraph
 * or an image gets. The agent's tables are still the one markdown construct
 * users want *out* of the chat, so the copy and download controls follow
 * directly under the grid, muted like the response action row.
 *
 * Cell text is read off the hast node react-markdown hands the component
 * rather than the rendered children, so the exported values match the source
 * cells regardless of how they are styled. Copy yields the markdown table back
 * — like CodeBlock, the copy control returns the source form — and download
 * yields CSV for the spreadsheet path.
 */

import { Suspense, useMemo } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconCheck, IconCopy, IconDownload } from '@tabler/icons-react';
import { LazyBoundary } from '~portal/components/common/LazyBoundary';
import { CmpC, downloadFile } from '~portal/lib';
import { retryableLazy } from '~portal/lib/retryable-lazy';
import { touchTarget } from '~portal/styles/touch-target.css';
import { typo } from '../typography.css';
import { type ColumnAlign, gridRows, mdColumns } from './md-table-columns';

/**
 * ag-Grid is a large dependency and most answers contain no table at all, so it
 * is fetched only once one does. Until the chunk lands the plain `<table>`
 * react-markdown already produced stands in as the Suspense fallback — the
 * reader sees the finished table immediately and it is simply replaced by the
 * sortable, resizable one, rather than flashing empty. A chunk that fails even after its retry
 * leaves that plain table in place for good (`LazyBoundary`): the reader loses sorting, not the
 * answer.
 */
const MdTableGrid = retryableLazy(() => import('./MdTableGrid'));

/** The slice of hast this module walks; avoids a direct `@types/hast` dep. */
export type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const nodeText = (node: HastNode): string => {
  if (node.type === 'text') return node.value ?? '';
  if (node.tagName === 'br') return ' ';
  return (node.children ?? []).map(nodeText).join('');
};

/** Row-major cell text for a hast `<table>`, header row first. */
export function tableRows(node: HastNode | undefined): string[][] {
  const rows: string[][] = [];
  const walk = (n: HastNode): void => {
    if (n.tagName === 'tr') {
      rows.push(
        (n.children ?? [])
          .filter((cell) => cell.tagName === 'th' || cell.tagName === 'td')
          .map((cell) => nodeText(cell).replace(/\s+/g, ' ').trim()),
      );
      return;
    }
    (n.children ?? []).forEach(walk);
  };
  if (node) walk(node);
  return rows;
}

const ALIGNMENTS: readonly string[] = ['left', 'center', 'right'];

/**
 * Per-column alignment as declared by the markdown delimiter row (`:---`,
 * `:---:`, `---:`), read off the header cells' `align` property — which is
 * where mdast-util-to-hast puts the table's `align` array. `undefined` for a
 * column the author left unaligned, so the caller can infer one instead.
 */
export function tableAlignments(node: HastNode | undefined): Array<ColumnAlign | undefined> {
  let header: HastNode | undefined;
  const walk = (n: HastNode): void => {
    if (header) return;
    if (n.tagName === 'tr') {
      header = n;
      return;
    }
    (n.children ?? []).forEach(walk);
  };
  if (node) walk(node);

  return (header?.children ?? [])
    .filter((cell) => cell.tagName === 'th' || cell.tagName === 'td')
    .map((cell) => {
      const { align } = cell.properties ?? {};
      return typeof align === 'string' && ALIGNMENTS.includes(align)
        ? (align as ColumnAlign)
        : undefined;
    });
}

/**
 * GFM table source, column-padded so it stays readable pasted as plain text.
 * Ragged rows are squared off and cell pipes escaped, so the result re-parses
 * as the same table. Column alignment markers are not reconstructed.
 */
export function toMarkdown(rows: string[][]): string {
  if (rows.length === 0) return '';
  const columns = Math.max(...rows.map((row) => row.length));
  const cells = rows.map((row) =>
    Array.from({ length: columns }, (_, i) => (row[i] ?? '').replace(/\|/g, '\\|')),
  );
  // Three is the narrowest a delimiter cell can be and still read as a rule
  const widths = Array.from({ length: columns }, (_, i) =>
    Math.max(3, ...cells.map((row) => row[i]?.length ?? 0)),
  );
  const line = (row: string[]) =>
    `| ${row.map((cell, i) => cell.padEnd(widths[i] ?? 0)).join(' | ')} |`;
  const [header = [], ...body] = cells;

  return [
    line(header),
    `| ${widths.map((w) => '-'.repeat(w)).join(' | ')} |`,
    ...body.map(line),
  ].join('\n');
}

/**
 * A cell opening with a formula sigil is neutralised with a leading apostrophe
 * (Excel's "this is text" marker) so a downloaded CSV can't execute agent or
 * warehouse content as a formula. Negative numbers are left alone — `-` only
 * leads a formula when what follows isn't a number.
 */
const NUMERIC = /^-?\d[\d,]*(\.\d+)?%?$/;

function csvCell(cell: string): string {
  const risky = /^[=+@\t\r]/.test(cell) || (cell.startsWith('-') && !NUMERIC.test(cell));
  const value = risky ? `'${cell}` : cell;
  return /["\n\r,]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function toCsv(rows: string[][]): string {
  return rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
}

/**
 * The table react-markdown rendered, unchanged. It is what the reader sees
 * before the grid chunk arrives, and the whole of what a table with no rows
 * (nothing to put in a grid) ever renders as.
 */
const PlainTable: CmpC<{ variant?: string }> = function ({ children, variant }) {
  return (
    <div className={typo.md.table.scroll}>
      <table className={typo.md.table.table} data-variant={variant}>
        {children}
      </table>
    </div>
  );
};

export type MdTableProps = {
  /** The hast `<table>` node, source of the exported cell values. */
  node?: HastNode;
  'data-variant'?: string;
};

export const MdTable: CmpC<MdTableProps> = function ({ node, children, 'data-variant': variant }) {
  const clipboard = useClipboard({ timeout: 1500 });
  const rows = tableRows(node);
  const columns = useMemo(() => mdColumns(rows, tableAlignments(node)), [node]);
  const data = useMemo(() => gridRows(rows), [node]);

  const handleDownload = (): void => {
    // The BOM keeps Excel on UTF-8 instead of guessing the local codepage.
    const blob = new Blob([`\uFEFF${toCsv(rows)}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    downloadFile(url, 'table.csv');
    // Revoked on the next tick: some browsers read the blob after the click
    // handler returns.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <div className={typo.md.table.wrapper} data-variant={variant}>
      {rows.length > 0 ? (
        /*
         * The same `overflow-x: auto` wrapper `PlainTable` uses. ag-Grid scrolls its own body
         * when the columns exceed the container, so this never produces a second scrollbar in
         * practice — it is the guard for the case the grid cannot honour: a table whose column
         * minimums (`DEFAULT_COL_DEF.minWidth`) already exceed a phone's prose column, where
         * without it the grid's own width pushes the page sideways.
         */
        <div className={typo.md.table.scroll}>
          <LazyBoundary
            name="MdTableGrid"
            fallback={() => <PlainTable variant={variant}>{children}</PlainTable>}
          >
            <Suspense fallback={<PlainTable variant={variant}>{children}</PlainTable>}>
              <MdTableGrid columns={columns} rows={data} />
            </Suspense>
          </LazyBoundary>
        </div>
      ) : (
        <PlainTable variant={variant}>{children}</PlainTable>
      )}
      {rows.length > 0 && (
        <div className={typo.md.table.actions}>
          <Tooltip label={clipboard.copied ? 'Copied' : 'Copy table'} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              className={touchTarget}
              aria-label="Copy table"
              onClick={() => clipboard.copy(toMarkdown(rows))}
            >
              {clipboard.copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Download CSV" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              className={touchTarget}
              aria-label="Download table as CSV"
              onClick={handleDownload}
            >
              <IconDownload size={16} />
            </ActionIcon>
          </Tooltip>
        </div>
      )}
    </div>
  );
};
